import { apiCreateFeatherQuote } from '@src/api/feather.api'
import { getChainNameFromChainId } from '@src/configs/chainInfo'
import { ChainId } from '@src/configs/chainInfo'
import { IFeatherApiQuotePayload, IFeatherFormattedQuoteResponse } from '@src/types/feather'
import { Address } from '@src/types/token'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('featherClient')

/**
 * FeatherClient handles communication with the Feather exchange backend service
 * which operates on a separate endpoint from the main API
 */
export class FeatherClient {
    private static instance: FeatherClient

    /**
     * Get the singleton instance of FeatherClient
     */
    public static getInstance(): FeatherClient {
        if (!FeatherClient.instance) {
            FeatherClient.instance = new FeatherClient()
        }
        return FeatherClient.instance
    }

    /**
     * Request a quote for exchanging tokens to Bitcoin
     * @param sellChainId The chain ID of the source token
     * @param sellTokenAddress The address of the source token
     * @param sellAmount The amount of source token (in smallest units)
     * @param buyChainId The chain ID of Bitcoin
     * @param destinationAddress The Bitcoin address where funds should be sent
     * @param tokenSymbol The symbol of the token to sell
     * @param sellTokenDecimals The number of decimals for the sell token
     * @param dstTokenSymbol The symbol of the destination token (default: 'BTC')
     * @param dstTokenDecimals The number of decimals for the destination token (default: 8 for Bitcoin)
     * @param userWalletAddress The user's wallet address (source wallet)
     */
    public async getQuote(
        sellChainId: number,
        sellTokenAddress: string,
        sellAmount: string,
        buyChainId: number,
        destinationAddress: string,
        tokenSymbol: string,
        sellTokenDecimals: number,
        dstTokenSymbol: string = 'BTC',
        dstTokenDecimals: number = 8,
        userWalletAddress?: string
    ): Promise<IFeatherFormattedQuoteResponse> {
        try {
            logger.info('Preparing Feather quote request', {
                sellChainId,
                sellTokenAddress,
                sellAmount,
                buyChainId,
                destinationAddress,
                tokenSymbol,
                sellTokenDecimals,
                dstTokenSymbol,
                dstTokenDecimals,
                userWalletAddress,
            })

            // Get chain names from chain IDs
            const srcChainName = getChainNameFromChainId(sellChainId as ChainId)
            const dstChainName = getChainNameFromChainId(buyChainId as ChainId)

            // Scale down the sellAmount by the token's decimals to get a decimal number
            // For example: 1234567 with 6 decimals becomes "1.234567"
            const sellAmountBigInt = BigInt(sellAmount)
            const decimalFactor = BigInt(10 ** sellTokenDecimals)

            // Calculate whole part and fractional part separately to avoid floating point issues
            const wholePart = sellAmountBigInt / decimalFactor
            const fractionalPart = sellAmountBigInt % decimalFactor

            // Convert to string with proper decimal formatting
            // Pad the fractional part with leading zeros if needed
            const fractionalPartStr = fractionalPart.toString().padStart(sellTokenDecimals, '0')
            const scaledSellAmount = `${wholePart}${fractionalPart > 0 ? `.${fractionalPartStr}` : ''}`

            logger.info('Scaled sell amount', {
                originalSellAmount: sellAmount,
                scaledSellAmount,
            })

            // Prepare the API payload
            const payload: IFeatherApiQuotePayload = {
                src_ticker: tokenSymbol,
                src_chain: srcChainName,
                dst_ticker: dstTokenSymbol,
                dst_chain: dstChainName,
                src_user_wallet: userWalletAddress!,
                dst_user_wallet: destinationAddress,
                src_amount: scaledSellAmount,
            }

            logger.info('Feather quote payload', { payload })
            const apiResponse = await apiCreateFeatherQuote(payload)

            logger.info('Processing Feather quote response', {
                quoteId: apiResponse.quote_id,
                srcAmount: apiResponse.src_amount,
                dstAmount: apiResponse.dst_amount,
            })

            // Process the response
            const expiresAt = new Date(apiResponse.expires_at).getTime() / 1000 // Convert ISO string to unix timestamp
            const createdAt = new Date(apiResponse.created_at).getTime() / 1000
            const validFor = expiresAt - createdAt // Calculate validFor in seconds

            // Handle destination amount scaling
            const dstAmount = apiResponse.dst_amount || '0'

            logger.info('Handling destination amount', {
                rawDstAmount: dstAmount,
                dstTokenDecimals,
            })

            // Convert scientific notation to decimal form
            let normalizedDstAmount: string
            if (dstAmount.includes('e')) {
                // Handle scientific notation (e.g., "3.399978809334148e-05")
                const floatValue = parseFloat(dstAmount)
                if (isNaN(floatValue)) {
                    normalizedDstAmount = '0'
                    logger.error('Failed to parse scientific notation', { dstAmount })
                } else {
                    // Convert to a fixed-point string with enough precision
                    // For negative exponents like e-05, we need at least that many decimal places
                    // plus the significant digits in the coefficient
                    const exponentMatch = dstAmount.match(/e(-?\d+)$/i)
                    const exponent = exponentMatch ? parseInt(exponentMatch[1]) : 0
                    // Use a precision that will capture all digits plus handle the exponent
                    const precision = Math.abs(exponent) + 15
                    normalizedDstAmount = floatValue.toFixed(precision)
                    logger.info('Converted scientific notation', {
                        dstAmount,
                        normalizedDstAmount,
                        exponent,
                        precision,
                    })
                }
            } else {
                normalizedDstAmount = dstAmount
            }

            // Handle decimal points in the normalized dst_amount, scaling it up by the destination token's decimals
            let scaledBuyAmount: string
            if (normalizedDstAmount.includes('.')) {
                const [wholePart, fractionalPart] = normalizedDstAmount.split('.')
                // Calculate how many zeros to add
                const padding = dstTokenDecimals - fractionalPart.length

                if (padding >= 0) {
                    // If the fractional part is shorter than dstTokenDecimals, add zeros
                    scaledBuyAmount = `${wholePart}${fractionalPart}${'0'.repeat(padding)}`
                } else {
                    // If the fractional part is longer than dstTokenDecimals (unusual), truncate
                    scaledBuyAmount = `${wholePart}${fractionalPart.substring(0, dstTokenDecimals)}`
                }
            } else {
                // No decimal point, just add zeros
                scaledBuyAmount = `${normalizedDstAmount}${'0'.repeat(dstTokenDecimals)}`
            }

            // Remove any leading zeros from the result
            scaledBuyAmount = scaledBuyAmount.replace(/^0+/, '') || '0'

            logger.info('Scaled buy amount', {
                rawDstAmount: dstAmount,
                normalizedDstAmount,
                scaledBuyAmount,
                dstTokenDecimals,
            })

            // Construct the formatted response
            const formattedResponse: IFeatherFormattedQuoteResponse = {
                quoteId: apiResponse.quote_id,
                sellAmount: apiResponse.src_amount,
                buyAmount: scaledBuyAmount, // Use the scaled up amount
                validFor: validFor,
                expiresAt: expiresAt,
                srcDepositWallet: apiResponse.src_deposit_wallet as Address,
            }

            logger.info('Feather quote prepared successfully', {
                quoteId: formattedResponse.quoteId,
                buyAmount: formattedResponse.buyAmount,
            })

            return formattedResponse
        } catch (error: any) {
            // Provide detailed error information
            const errorDetails = {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                code: error.code,
            }

            logger.error('Error fetching Feather quote:', errorDetails)
            throw error
        }
    }
}

// Export a singleton instance
export const featherClient = FeatherClient.getInstance()
